/**
 * The class action plan — one paper, one class, grouped by topic.
 *
 * This is the teacher's half of the report pair. The student's mastery sheet
 * answers "how did I do"; this answers the only two questions a teacher can act
 * on before Monday — WHICH topic do I go back to, and WHO do I go back to it
 * with. So the paper is cut by topic rather than by question order, the topics
 * are ranked, and every row carries the band distribution and the names.
 *
 * It is the schools' printed Teacher Report re-cut to CWY's five bands. The
 * original spends four columns on 90-100 / 60-90 / 0-60 / QNA; those become the
 * five bands plus QNA, because a band already says how the mark was earned and a
 * percentage cannot — 100% on the second attempt is Blue, and the printed report
 * files it next to a first-attempt solve.
 *
 * The bands ARE the reading order, as on the mastery sheet: a topic's heading
 * takes the colour of the middle of the class, and the ranking runs best to
 * worst, with the work lifted back to the top by the "Start here" strip.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from 'recharts';
import { apiGet } from '@/lib/api';
import { BIN_FILL, BIN_LABEL, BIN_ORDER, binRamp } from '@/lib/bins';
import { ReportSkeleton } from '@/components/reports/ReportSkeleton';
import {
  BAND_ACTION,
  BAND_ACTION_FULL,
  STANDING_NOTE,
  WORK_ORDER,
  largestRemainder,
  type BandStudent,
  type BinColor,
  type ClassActionPlanReport,
  type ClassQuestionRow,
  type ClassTopicSection,
  type ReteachBin,
} from '@cwy/config/contracts';
import {
  BandLegend,
  BandRamp,
  DS_BIN,
  FooterStat,
  gradeLabel,
  HeaderField,
  MODE_LABEL,
  SheetFrame,
  StartHere,
  TYPE_LABEL,
  TickBox,
  TopicHeading,
  TopicRankBars,
  fmtDate,
} from '@/components/reports/ActionPlanKit';
import { BinGlyph } from '@/components/ds';
import { HELPLINE_BLURB, HELPLINE_LABEL, HELPLINE_ORDER, type HelplineKind } from '@/lib/helplines';
import { HelplineStack, panelKey, type OpenPanel } from '@/components/teacher/HelplineStack';
import { fontPx } from '@/lib/fontSize';

/**
 * Pink to Green — the teacher reads this sheet the other way round from a
 * student's own copy. Nothing else on the page is worth reordering for that
 * (a proportional strip has no reading order), but every column list, chart
 * and legend below does, so they all read the weakest band first.
 */
const TEACHER_BIN_ORDER: BinColor[] = [...BIN_ORDER].reverse();

/** The sheet's column order, and the width each one is allowed to take. */
const COLUMNS: { key: string; label: string; width: number; align?: 'center' }[] = [
  // Band is a shape, not a word — the glyph and the fill say which one, and the
  // legend at the foot says what it means. Printing "Orange" beside an orange
  // triangle on an orange row was the label saying it a third time.
  { key: 'band', label: 'Band', width: 40, align: 'center' },
  { key: 'q', label: 'Q', width: 46 },
  { key: 'mm', label: 'MM', width: 40, align: 'center' },
  { key: 'avg', label: 'Av. %', width: 52, align: 'center' },
  { key: 'time', label: 'Time · set / taken', width: 104, align: 'center' },
  { key: 'descriptor', label: 'Sub-topic & descriptor', width: 186 },
];

// The Next action column is gone: BAND_ACTION says the same sentence in the
// legend below, once, instead of repeating it on every row of the same band.
const TAIL: { key: string; label: string; width: number; align?: 'center' }[] = [
  { key: 'qna', label: 'QNA', width: 42, align: 'center' },
  { key: 'names', label: 'Re-teach to these students', width: 204 },
  // The client asked for four helpline COLUMNS here. Four of them cost ~184px
  // and put the sheet back into sideways scrolling, which they asked for in the
  // same breath not to do. One column carrying all four openers costs 150px and
  // keeps both asks — the four are still per question, still on the right, and
  // each still opens its own panel.
  { key: 'helplines', label: 'Helplines', width: 138 },
];

/** Minutes, rounded — a class report is planned in minutes, not seconds. */
function mins(seconds: number): string {
  return `${Math.round(seconds / 60)}m`;
}

/**
 * What the paper allowed against what the class spent.
 *
 * The variation is the point, so it is the loud part: over its allocation is
 * the signal that a question cost more of the lesson than it was worth, and
 * well under it usually means they gave up rather than raced.
 */
function TimeSpent({ allocated, average }: { allocated: number; average: number }) {
  if (!average) {
    return (
      <span className="text-xs" style={{ color: 'var(--ink-3)' }}>
        —
      </span>
    );
  }
  const deltaPct = Math.round(((average - allocated) / allocated) * 100);
  const over = deltaPct > 0;
  return (
    <span className="flex flex-col items-center leading-tight">
      <span data-numeric="" className="text-fs-11.5" style={{ color: 'var(--ink-2)' }}>
        {mins(allocated)} / <strong style={{ color: 'var(--text-body)' }}>{mins(average)}</strong>
      </span>
      <span
        data-numeric=""
        className="text-fs-10 font-bold"
        style={{ color: over ? 'var(--orange-deep)' : 'var(--ink-3)' }}
      >
        {deltaPct > 0 ? '+' : ''}
        {deltaPct}%
      </span>
    </span>
  );
}

/**
 * The share of the class in each band, as a number.
 *
 * The ramp above it is a proportional strip, which answers "roughly how much"
 * and nothing else — the counts were only ever in its `aria-label`, so a sighted
 * teacher could not read them at all. Percentages rather than counts because
 * the question is what share of the room, and a room is not always twenty.
 *
 * Rounded by largest remainder so the five add to 100. Five independently
 * rounded percentages that total 99 is the kind of thing a teacher spots and
 * then stops trusting the rest of the sheet over.
 */
function BandShare({ counts }: { counts: Record<BinColor, number> }) {
  const total = TEACHER_BIN_ORDER.reduce((sum, b) => sum + counts[b], 0);
  if (!total) return null;

  const wholes = largestRemainder(
    TEACHER_BIN_ORDER.map((bin) => counts[bin]),
    100,
  );
  const shares = TEACHER_BIN_ORDER.map((bin, i) => ({ bin, whole: wholes[i] }));

  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-0.5">
      {shares.map(({ bin, whole }) => (
        <span
          key={bin}
          className="flex items-center gap-1 text-fs-10 font-bold"
          style={{ color: binRamp(bin).text }}
          title={`${BIN_LABEL[bin]} — ${counts[bin]} of ${total} answers`}
        >
          <BinGlyph bin={DS_BIN[bin]} size={8} color="currentColor" title={null} />
          <span data-numeric="">{whole}%</span>
        </span>
      ))}
    </div>
  );
}

/** The four helplines for one question, each opening its own stacked panel. */
function HelplineCell({
  row,
  onOpen,
}: {
  row: ClassQuestionRow;
  onOpen: (questionId: string, qNumber: number, kind: HelplineKind) => void;
}) {
  return (
    <span className="flex flex-wrap gap-1 print:hidden">
      {HELPLINE_ORDER.map((kind) => (
        <button
          key={kind}
          type="button"
          onClick={() => onOpen(row.questionId, row.qNumber, kind)}
          title={`${HELPLINE_LABEL[kind]} — ${HELPLINE_BLURB[kind]}`}
          className="rounded-full px-2 py-0.5 text-fs-10 font-bold transition hover:opacity-80"
          style={{
            background: 'var(--surface-card)',
            border: '1px solid var(--border-default)',
            color: 'var(--ink-2)',
          }}
        >
          {HELPLINE_LABEL[kind]}
        </button>
      ))}
    </span>
  );
}

/** One direction of travel through the bands, as shapes rather than words. */
function WorkOrder({ label, order, note }: { label: string; order: BinColor[]; note?: string }) {
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <span className="font-bold uppercase tracking-wide" style={{ color: 'var(--ink-3)' }}>
        {label}
      </span>
      {order.map((bin, i) => (
        <span key={bin} className="flex items-center gap-1.5">
          {i > 0 && <span style={{ color: 'var(--ink-3)' }}>→</span>}
          <span className="flex items-center gap-1" style={{ color: binRamp(bin).text }}>
            <BinGlyph bin={DS_BIN[bin]} size={9} color="currentColor" title={null} />
            <span className="font-bold">{BIN_LABEL[bin]}</span>
          </span>
        </span>
      ))}
      {note && <span style={{ color: 'var(--ink-3)' }}>— {note}</span>}
    </span>
  );
}

/** One band's count on a question. A zero is a dash — see the mastery sheet. */
function Count({ value, bin }: { value: number; bin: BinColor }) {
  if (!value) {
    return (
      <span className="text-xs" style={{ color: 'var(--ink-3)' }}>
        —
      </span>
    );
  }
  const ramp = binRamp(bin);
  return (
    <span
      data-numeric=""
      className="inline-flex h-6 min-w-6 items-center justify-center rounded-md px-1.5 text-xs font-bold"
      style={{
        background: 'var(--surface-card)',
        color: ramp.border,
        border: `1px solid ${ramp.border}`,
      }}
    >
      {value}
    </span>
  );
}

/**
 * Who the re-teach is for.
 *
 * Named, not counted: "8 students" sends a teacher back to the matrix to find
 * out which eight, and that round trip is the reason the printed report lists
 * them. Past half the class the list stops being useful and the instruction
 * changes — that is the original's ORANGE legend, made explicit.
 */
function ReteachList({
  row,
  onOpenStudent,
}: {
  row: ClassQuestionRow;
  onOpenStudent?: (studentId: string) => void;
}) {
  if (row.reteachStudents.length === 0) {
    return (
      <span className="text-fs-11.5" style={{ color: 'var(--ink-3)' }}>
        Clean — nobody needs this one again
      </span>
    );
  }
  return (
    <>
      {row.wholeClass && (
        // Half the room or more: this is not a list of names any more, it is
        // feedback to the whole class at once — the client's "collective
        // feedback", which is the accurate name for it.
        <span
          className="mb-1 inline-block rounded-full px-2 py-0.5 text-fs-10 font-bold uppercase tracking-wide"
          style={{ background: 'var(--orange-fill)', color: 'var(--orange-deep)' }}
          title="Half the class or more needs this one — take it back to everyone at once."
        >
          Collective feedback · whole class
        </span>
      )}
      <span className="block text-fs-11 leading-snug" style={{ color: 'var(--ink-2)' }}>
        {row.reteachStudents.map((s, i) => (
          <span
            key={s.studentId}
            className="font-semibold"
            style={{ color: TONE[s.bin].text }}
            title={BIN_LABEL[s.bin]}
          >
            {i > 0 && <span style={{ color: 'var(--ink-3)' }}> · </span>}
            {onOpenStudent ? (
              <button
                type="button"
                onClick={() => onOpenStudent(s.studentId)}
                className="underline-offset-2 hover:underline print:no-underline"
                style={{ color: 'inherit' }}
              >
                {s.name}
              </button>
            ) : (
              s.name
            )}
          </span>
        ))}
      </span>
    </>
  );
}

/** The two re-teach bands' colour stops. */
const TONE: Record<
  ReteachBin,
  { bg: string; border: string; text: string; fill: string; deep: string }
> = {
  PINK: {
    bg: 'var(--pink-bg)',
    border: 'var(--pink-border)',
    text: 'var(--pink-text)',
    fill: 'var(--pink-fill)',
    deep: 'var(--pink-deep)',
  },
  ORANGE: {
    bg: 'var(--orange-bg)',
    border: 'var(--orange-border)',
    text: 'var(--orange-text)',
    fill: 'var(--orange-fill)',
    deep: 'var(--orange-deep)',
  },
};

/** What each group means, and what to do with it. Pink first: it is the worse. */
const SPLIT: { bin: ReteachBin; title: string; meaning: string }[] = [
  {
    bin: 'PINK',
    title: 'Pink — not solved yet',
    meaning: 'At least one question still unsolved after three attempts.',
  },
  {
    bin: 'ORANGE',
    title: 'Orange — solved on the third attempt',
    meaning: 'Nothing left unsolved, but some only came on the last try.',
  },
];

function QuestionChips({ numbers, bin }: { numbers: number[]; bin: ReteachBin }) {
  return (
    <>
      {numbers.map((q) => (
        <span
          key={q}
          data-numeric=""
          className="rounded-md px-1.5 py-0.5 text-fs-10.5 font-bold"
          style={{ background: TONE[bin].fill, color: TONE[bin].deep }}
          title={`Q${q} — ${BIN_LABEL[bin]}`}
        >
          Q{q}
        </span>
      ))}
    </>
  );
}

function BandGroup({
  bin,
  title,
  meaning,
  students,
  onOpenStudent,
}: {
  bin: ReteachBin;
  title: string;
  meaning: string;
  students: BandStudent[];
  onOpenStudent?: (studentId: string) => void;
}) {
  const tone = TONE[bin];
  return (
    <section
      className="flex flex-col rounded-[14px]"
      style={{ background: tone.bg, border: `1px solid ${tone.border}` }}
      aria-label={title}
    >
      <header className="px-3 py-2.5" style={{ borderBottom: `1px solid ${tone.border}` }}>
        <div className="flex items-center gap-2">
          <span style={{ color: tone.text }}>
            <BinGlyph bin={DS_BIN[bin]} size={10} color="currentColor" title={null} />
          </span>
          <h4 className="text-sm font-bold" style={{ color: tone.deep }}>
            {title}
          </h4>
          <span
            data-numeric=""
            className="ml-auto rounded-full px-2 py-0.5 text-fs-11 font-bold"
            style={{ background: tone.fill, color: tone.deep }}
          >
            {students.length} {students.length === 1 ? 'student' : 'students'}
          </span>
        </div>
        <p className="mt-1 text-fs-11" style={{ color: tone.text }}>
          {meaning} <strong>{BAND_ACTION[bin]}.</strong>
        </p>
      </header>

      {students.length === 0 ? (
        <p className="px-3 py-4 text-xs" style={{ color: 'var(--ink-3)' }}>
          Nobody on this paper.
        </p>
      ) : (
        <ul>
          {students.map((s, i) => (
            <li
              key={s.studentId}
              className="flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-2"
              style={i > 0 ? { borderTop: `1px solid ${tone.border}` } : undefined}
            >
              <span data-numeric="" className="w-6 text-fs-11" style={{ color: 'var(--ink-3)' }}>
                {s.rollNumber}
              </span>
              {onOpenStudent ? (
                <button
                  type="button"
                  onClick={() => onOpenStudent(s.studentId)}
                  className="text-sm font-semibold underline-offset-2 hover:underline print:no-underline"
                  style={{ color: 'var(--text-strong)' }}
                >
                  {s.name}
                </button>
              ) : (
                <span className="text-sm font-semibold" style={{ color: 'var(--text-strong)' }}>
                  {s.name}
                </span>
              )}
              <span className="ml-auto flex flex-wrap justify-end gap-1">
                <QuestionChips numbers={s.pink} bin="PINK" />
                <QuestionChips numbers={s.orange} bin="ORANGE" />
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * The class bifurcated into Pink and Orange — who the teacher goes back to.
 *
 * The per-question "Re-teach to" lists answer "who, for this question"; this
 * answers "who, for this paper", which is the list a teacher calls a group
 * from. Each child appears once, under the worse band they reached, with every
 * question that put them there.
 */
function BandSplit({
  data,
  onOpenStudent,
}: {
  data: ClassActionPlanReport;
  onOpenStudent?: (studentId: string) => void;
}) {
  const clear = data.clearOfReteach;
  return (
    <div style={{ borderTop: '1.5px solid var(--border-strong)' }}>
      <div className="px-4 pt-3">
        <p
          className="text-fs-10 font-bold uppercase tracking-[0.08em]"
          style={{ color: 'var(--ink-3)' }}
        >
          Who needs you — the class in Pink and Orange
        </p>
        <p className="mt-0.5 text-xs" style={{ color: 'var(--ink-3)' }}>
          Each student is listed once, under the worse band they reached on this paper, with the
          questions that put them there.{' '}
          {clear > 0
            ? `${clear} ${clear === 1 ? 'student has' : 'students have'} nothing in either.`
            : 'Every student has something in one or the other.'}
        </p>
      </div>
      <div className="grid gap-3 p-4 md:grid-cols-2">
        {SPLIT.map((group) => (
          <BandGroup
            key={group.bin}
            {...group}
            students={data.bandStudents[group.bin]}
            onOpenStudent={onOpenStudent}
          />
        ))}
      </div>
    </div>
  );
}

interface TopicBandDatum {
  topicId: string;
  topicName: string;
  /** Raw answers per band — what the tooltip reports. */
  counts: Record<BinColor, number>;
  /** How many answers are behind this bar. */
  total: number;
  // The BARS carry share, not count. A topic with five questions used to draw a
  // bar two and a half times the length of a three-question topic at identical
  // class performance, and length is the one thing a stacked bar invites you to
  // compare across rows. The counts live on, in the tooltip and in the sheet.
  GREEN: number;
  PURPLE: number;
  BLUE: number;
  ORANGE: number;
  PINK: number;
}

function BandTooltip({ active, payload }: TooltipProps<number, string>) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload as TopicBandDatum;
  const total = d.total;
  return (
    <div
      className="rounded-lg px-3 py-2 text-xs text-white"
      style={{ background: 'var(--brand-forest)', boxShadow: 'var(--shadow-lg)' }}
    >
      <p className="mb-1 font-bold">{d.topicName}</p>
      {TEACHER_BIN_ORDER.filter((b) => d.counts[b] > 0).map((b) => (
        <p key={b}>
          {BIN_LABEL[b]}: {d.counts[b]} of {total} · {Math.round(d[b])}%
        </p>
      ))}
    </div>
  );
}

/**
 * How the class split across the five bands, topic by topic.
 *
 * Every row already carries a `BandRamp` proportional strip, but a strip has
 * no axis and no per-band count on hover — this is the one place on the sheet
 * a teacher can read "6 of 22 in Pink on Trigonometry" without doing the sum
 * by eye. Stacked Pink nearest the axis, Green furthest — the teacher's own
 * reading order, same as the rest of this sheet.
 *
 * Every bar runs 0–100%, because the question the title asks — how did the
 * class SPLIT, topic by topic — is a part-to-whole one, and topics carry
 * different numbers of questions. On counts, the longest bar meant the topic
 * with the most questions on the paper, which is not a fact about the class.
 */
function TopicBandChart({ topics }: { topics: ClassTopicSection[] }) {
  const data: TopicBandDatum[] = topics.map((t) => {
    const total = BIN_ORDER.reduce((sum, b) => sum + t.binCounts[b], 0) || 1;
    const share = (bin: BinColor) => (t.binCounts[bin] / total) * 100;
    return {
      topicId: t.topicId,
      topicName: t.topicName,
      counts: t.binCounts,
      total,
      GREEN: share('GREEN'),
      PURPLE: share('PURPLE'),
      BLUE: share('BLUE'),
      ORANGE: share('ORANGE'),
      PINK: share('PINK'),
    };
  });
  const height = Math.max(160, data.length * 40);

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1">
        {TEACHER_BIN_ORDER.map((bin) => (
          <span
            key={bin}
            className="flex items-center gap-1.5 text-fs-11 font-bold"
            style={{ color: binRamp(bin).text }}
          >
            <BinGlyph bin={DS_BIN[bin]} size={9} color="currentColor" title={null} />
            {BIN_LABEL[bin]}
          </span>
        ))}
      </div>
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ left: 8, right: 16 }}>
            <XAxis
              type="number"
              domain={[0, 100]}
              ticks={[0, 25, 50, 75, 100]}
              tickFormatter={(v: number) => `${v}%`}
              tick={{ style: { fontSize: fontPx(11) } }}
              stroke="var(--ink-3)"
            />
            <YAxis
              type="category"
              dataKey="topicName"
              width={140}
              tick={{ style: { fontSize: fontPx(11) } }}
              stroke="var(--ink-3)"
            />
            <Tooltip content={<BandTooltip />} cursor={{ fill: 'var(--paper-sunk)' }} />
            {TEACHER_BIN_ORDER.map((bin, i) => (
              <Bar
                key={bin}
                dataKey={bin}
                stackId="bands"
                fill={BIN_FILL[bin]}
                stroke="var(--surface-card)"
                strokeWidth={2}
                radius={i === TEACHER_BIN_ORDER.length - 1 ? [0, 4, 4, 0] : undefined}
                isAnimationActive
                animationDuration={300}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function ClassActionPlan({
  paperId,
  classroomId,
  onOpenStudent,
}: {
  paperId: string;
  /** The class this sheet reads — a paper is read one class at a time. */
  classroomId: string;
  /** Every name on the sheet opens that child's own report. */
  onOpenStudent?: (studentId: string) => void;
}) {
  // Several panels can be open at once — that is the whole point of the stack.
  // Keyed per question AND kind, so re-clicking the same opener re-focuses it
  // rather than stacking a duplicate on top of itself.
  const [panels, setPanels] = useState<OpenPanel[]>([]);
  const openHelpline = (questionId: string, qNumber: number, kind: HelplineKind) =>
    setPanels((prev) => {
      const key = panelKey(questionId, kind);
      if (prev.some((p) => p.key === key)) return prev;
      return [...prev, { key, questionId, qNumber, kind }];
    });
  const closeHelpline = (key: string) => setPanels((prev) => prev.filter((p) => p.key !== key));

  const { data, isLoading, isError } = useQuery({
    queryKey: ['class-action-plan', paperId, classroomId],
    enabled: Boolean(paperId && classroomId),
    queryFn: () =>
      apiGet<ClassActionPlanReport>(
        `/teacher/papers/${paperId}/class-action-plan?classroomId=${encodeURIComponent(classroomId)}`,
      ),
  });

  if (isLoading) return <ReportSkeleton variant="table" />;
  if (isError || !data) {
    return (
      <p className="p-6 text-center" style={{ color: 'var(--ink-3)' }}>
        This paper has no class plan yet — nobody has sat it.
      </p>
    );
  }

  const { header, topics, footer, binCounts } = data;

  return (
    <>
      <SheetFrame id="cwy-class-action-plan">
        {/* ── Name strip ── */}
        <div
          className="flex flex-wrap items-center gap-x-6 gap-y-3 px-4 py-3"
          style={{
            borderBottom: '1.5px solid var(--border-strong)',
            background: 'var(--paper-sunk)',
          }}
        >
          <div className="grid min-w-0 flex-1 grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
            <HeaderField label="Class / Section" value={header.gradeSection} />
            <HeaderField label="Subject" value={header.subject} />
            <HeaderField label="Paper" value={header.paperTitle} />
            <HeaderField label="Paper code" value={header.paperCode} />
            <HeaderField label="Class" value={header.className} />
            <HeaderField label="Subject teacher" value={header.subjectTeacher} />
            <HeaderField label="Principal" value={header.principal} />
            <HeaderField label="Date" value={fmtDate(header.date)} />
          </div>

          <div className="flex flex-none items-center gap-3">
            <div className="text-right">
              <div
                data-numeric=""
                className="text-xl font-extrabold leading-none"
                style={{ color: 'var(--text-strong)' }}
              >
                {data.classAveragePercent}
                <span className="text-sm font-bold" style={{ color: 'var(--ink-3)' }}>
                  % class average
                </span>
              </div>
              <div className="mt-1 w-52">
                <BandRamp counts={binCounts} />
                <BandShare counts={binCounts} />
              </div>
              <div
                data-numeric=""
                className="mt-1 text-fs-10 font-bold uppercase tracking-wide"
                style={{ color: 'var(--ink-3)' }}
              >
                {data.submitted} of {data.totalStudents} submitted
              </div>
            </div>
            <span
              className="shrink-0 rounded-full px-3 py-1 text-xs font-bold"
              style={{ background: 'var(--slab)', color: 'var(--text-on-forest)' }}
            >
              Grade {gradeLabel(header.grade)} · {header.board} ·{' '}
              {MODE_LABEL[header.mode] ?? header.mode}
            </span>
          </div>
        </div>

        <StartHere title="Start here — the weakest topics on this paper" items={data.startHere} />

        {/* ── The ranking ── */}
        <div className="px-4 py-3" style={{ borderBottom: '1.5px solid var(--border-strong)' }}>
          <p
            className="mb-2 text-fs-10 font-bold uppercase tracking-[0.08em]"
            style={{ color: 'var(--ink-3)' }}
          >
            Topics, strongest first
          </p>
          <TopicRankBars topics={topics} />
        </div>

        {/* ── One block per topic ── */}
        {topics.map((topic, index) => (
          <div key={topic.topicId} style={{ borderBottom: '1.5px solid var(--border-strong)' }}>
            <TopicHeading
              index={index + 1}
              topicName={topic.topicName}
              averagePercent={topic.averagePercent}
              bin={topic.bin}
              right={
                <span className="hidden w-32 sm:block">
                  <BandRamp counts={topic.binCounts} height={8} />
                </span>
              }
            />

            {/* Desktop: the ruled grid. */}
            <div className="hidden overflow-x-auto sm:block">
              <table
                className="text-left text-fs-12.5"
                style={{ borderCollapse: 'separate', borderSpacing: 0, minWidth: '100%' }}
              >
                <thead>
                  <tr>
                    {COLUMNS.map((c) => (
                      <th
                        key={c.key}
                        className="px-2 py-2 align-bottom text-fs-10 font-bold uppercase leading-tight tracking-[0.06em]"
                        style={{
                          width: c.width,
                          minWidth: c.width,
                          color: 'var(--ink-2)',
                          background: 'var(--paper-sunk)',
                          textAlign: c.align ?? 'left',
                          borderBottom: '1.5px solid var(--border-strong)',
                          borderRight: '1px solid var(--border-subtle)',
                        }}
                      >
                        {c.label}
                      </th>
                    ))}
                    {TEACHER_BIN_ORDER.map((bin) => (
                      <th
                        key={bin}
                        title={`${BIN_LABEL[bin]} — how many of the class landed here`}
                        className="px-1 py-2 align-bottom"
                        style={{
                          width: 46,
                          minWidth: 46,
                          background: 'var(--paper-sunk)',
                          borderBottom: '1.5px solid var(--border-strong)',
                          borderRight: '1px solid var(--border-subtle)',
                        }}
                      >
                        <span className="flex flex-col items-center gap-0.5">
                          <BinGlyph bin={DS_BIN[bin]} size={9} title={null} />
                          <span
                            className="text-fs-9 font-bold uppercase tracking-tight"
                            style={{ color: binRamp(bin).text }}
                          >
                            {BIN_LABEL[bin]}
                          </span>
                        </span>
                      </th>
                    ))}
                    {TAIL.map((c, i) => (
                      <th
                        key={c.key}
                        className="px-2 py-2 align-bottom text-fs-10 font-bold uppercase leading-tight tracking-[0.06em]"
                        style={{
                          width: c.width,
                          minWidth: c.width,
                          color: 'var(--ink-2)',
                          background: 'var(--paper-sunk)',
                          textAlign: c.align ?? 'left',
                          borderBottom: '1.5px solid var(--border-strong)',
                          borderRight:
                            i === TAIL.length - 1 ? undefined : '1px solid var(--border-subtle)',
                        }}
                      >
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {topic.rows.map((row) => {
                    const ramp = binRamp(row.bin);
                    const cell = {
                      background: ramp.bg,
                      borderBottom: '1px solid var(--border-subtle)',
                      borderRight: '1px solid var(--border-subtle)',
                    };
                    return (
                      <tr key={row.questionId}>
                        <td className="px-2 py-2 text-center align-top" style={cell}>
                          <BinGlyph
                            bin={DS_BIN[row.bin]}
                            size={11}
                            title={`${BIN_LABEL[row.bin]} — ${BAND_ACTION[row.bin]}`}
                          />
                        </td>
                        <td className="px-2 py-2 align-top" style={cell}>
                          <span
                            data-numeric=""
                            className="font-extrabold"
                            style={{ color: 'var(--text-strong)' }}
                          >
                            {row.qNumber}
                          </span>
                          <span
                            className="mt-0.5 block text-fs-10 font-semibold"
                            style={{ color: 'var(--ink-3)' }}
                          >
                            {TYPE_LABEL[row.type] ?? row.type}
                          </span>
                        </td>
                        <td
                          data-numeric=""
                          className="px-2 py-2 text-center align-top font-semibold"
                          style={{ ...cell, color: 'var(--text-body)' }}
                        >
                          {row.maxMarks}
                        </td>
                        <td
                          data-numeric=""
                          className="px-2 py-2 text-center align-top font-extrabold"
                          style={{ ...cell, color: ramp.text }}
                        >
                          {row.averagePercent}
                        </td>
                        <td className="px-1 py-2 text-center align-top" style={cell}>
                          <TimeSpent
                            allocated={row.allocatedSeconds}
                            average={row.averageSeconds}
                          />
                        </td>
                        <td className="px-2 py-2 align-top" style={cell}>
                          <span
                            className="block font-semibold"
                            style={{ color: 'var(--text-body)' }}
                          >
                            {row.subtopicName}
                          </span>
                          <span
                            className="mt-0.5 block text-fs-11.5"
                            style={{ color: 'var(--ink-2)' }}
                          >
                            {row.descriptor}
                          </span>
                          {row.commonError && (
                            <span
                              className="mt-1 block text-fs-10.5"
                              style={{ color: 'var(--ink-3)' }}
                            >
                              Common slip: {row.commonError.toLowerCase()}
                            </span>
                          )}
                        </td>

                        {TEACHER_BIN_ORDER.map((bin) => (
                          <td key={bin} className="px-1 py-2 text-center align-top" style={cell}>
                            <Count value={row.binCounts[bin]} bin={bin} />
                          </td>
                        ))}

                        <td
                          data-numeric=""
                          className="px-1 py-2 text-center align-top font-semibold"
                          style={{
                            ...cell,
                            color: row.notAttempted ? 'var(--ink-2)' : 'var(--ink-3)',
                          }}
                        >
                          {row.notAttempted || '—'}
                        </td>

                        <td
                          className="px-2 py-2 align-top"
                          style={{ ...cell, borderRight: undefined }}
                        >
                          <span className="flex items-start gap-2">
                            <TickBox label={`Mark ${BAND_ACTION[row.bin]} as done`} />
                            <span className="flex-1">
                              <ReteachList row={row} onOpenStudent={onOpenStudent} />
                            </span>
                          </span>
                        </td>

                        <td
                          className="px-2 py-2 align-top"
                          style={{ ...cell, borderRight: undefined }}
                        >
                          <HelplineCell row={row} onOpen={openHelpline} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Phone: the same rows, one card each. */}
            <div className="sm:hidden">
              {topic.rows.map((row) => {
                const ramp = binRamp(row.bin);
                return (
                  <div
                    key={row.questionId}
                    className="px-3 py-2.5"
                    style={{ background: ramp.bg, borderTop: '1px solid var(--border-subtle)' }}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span
                        data-numeric=""
                        className="text-sm font-extrabold"
                        style={{ color: 'var(--text-strong)' }}
                      >
                        Q{row.qNumber}
                        <span
                          className="ml-1.5 text-fs-10 font-semibold"
                          style={{ color: 'var(--ink-3)' }}
                        >
                          {TYPE_LABEL[row.type] ?? row.type} · {row.maxMarks} marks
                        </span>
                      </span>
                      <span
                        data-numeric=""
                        className="text-sm font-extrabold"
                        style={{ color: ramp.text }}
                      >
                        {row.averagePercent}%
                      </span>
                    </div>

                    <p
                      className="mt-0.5 text-fs-12 font-semibold"
                      style={{ color: 'var(--text-body)' }}
                    >
                      {row.subtopicName}
                    </p>
                    <p className="text-fs-11.5" style={{ color: 'var(--ink-2)' }}>
                      {row.descriptor}
                    </p>

                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                      {TEACHER_BIN_ORDER.filter((bin) => row.binCounts[bin] > 0).map((bin) => (
                        <span
                          key={bin}
                          className="flex items-center gap-1 text-fs-10 font-bold uppercase tracking-wide"
                          style={{ color: 'var(--ink-3)' }}
                        >
                          {BIN_LABEL[bin]} <Count value={row.binCounts[bin]} bin={bin} />
                        </span>
                      ))}
                      {row.notAttempted > 0 && (
                        <span
                          className="text-fs-10 font-bold uppercase tracking-wide"
                          style={{ color: 'var(--ink-3)' }}
                        >
                          QNA {row.notAttempted}
                        </span>
                      )}
                    </div>

                    <p className="mt-1.5 text-fs-11" style={{ color: 'var(--ink-2)' }}>
                      <span data-numeric="">
                        {mins(row.allocatedSeconds)} set · {mins(row.averageSeconds)} taken
                      </span>
                    </p>
                    <div className="mt-0.5">
                      <ReteachList row={row} onOpenStudent={onOpenStudent} />
                    </div>
                    <div className="mt-1.5">
                      <HelplineCell row={row} onOpen={openHelpline} />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ── This chapter's own totals ── */}
            <div
              className="grid grid-cols-2 sm:grid-cols-4"
              style={{
                borderTop: '1.5px solid var(--border-strong)',
                background: 'var(--paper-sunk)',
              }}
            >
              <FooterStat label="Chapter average" value={`${topic.averagePercent}%`} />
              <FooterStat
                label="Questions to re-teach"
                value={String(topic.footer.questionsToReteach)}
                tone={topic.footer.questionsToReteach ? 'var(--orange-text)' : undefined}
              />
              <FooterStat label="Peer-ready" value={String(topic.footer.peerReady)} />
              <FooterStat
                label="Not attempted"
                value={String(topic.footer.notAttempted)}
                tone={topic.footer.notAttempted ? 'var(--pink-text)' : undefined}
              />
            </div>
            <div
              className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-4 py-2.5"
              style={{
                borderTop: '1px solid var(--border-subtle)',
                background: 'var(--brand-lime-soft)',
              }}
            >
              <span
                className="text-fs-11 font-bold uppercase tracking-[0.06em]"
                style={{ color: 'var(--brand-ink)' }}
              >
                Marks waiting for this chapter
              </span>
              <span
                data-numeric=""
                className="text-base font-extrabold leading-none"
                style={{ color: 'var(--brand-ink)' }}
              >
                {topic.footer.marksLost}
              </span>
              <span className="text-xs" style={{ color: 'var(--ink-2)' }}>
                {topic.footer.marksLostNote}
              </span>
            </div>
          </div>
        ))}

        {/* ── Overall: the whole paper, after every chapter's own report ── */}
        <div
          className="mt-4 px-4 py-2.5"
          style={{
            borderTop: '1.5px solid var(--border-strong)',
            borderBottom: '1.5px solid var(--border-strong)',
            background: 'var(--slab)',
          }}
        >
          <h3
            className="text-xs font-bold uppercase tracking-[0.08em]"
            style={{ color: 'var(--text-on-forest)' }}
          >
            Overall — the whole paper
          </h3>
        </div>

        <div
          className="grid grid-cols-3 sm:grid-cols-6"
          style={{ borderTop: '1.5px solid var(--border-strong)', background: 'var(--paper-sunk)' }}
        >
          <FooterStat label="Class average" value={`${data.classAveragePercent}%`} />
          <FooterStat label="Submitted" value={`${data.submitted}/${data.totalStudents}`} />
          <FooterStat
            label="Topics to re-teach"
            value={String(footer.topicsToReteach)}
            tone={footer.topicsToReteach ? 'var(--orange-text)' : undefined}
          />
          <FooterStat
            label="Questions to re-teach"
            value={String(footer.questionsToReteach)}
            tone={footer.questionsToReteach ? 'var(--orange-text)' : undefined}
          />
          <FooterStat label="Peer-ready" value={String(footer.peerReady)} />
          <FooterStat
            label="Not attempted"
            value={String(footer.notAttempted)}
            tone={footer.notAttempted ? 'var(--pink-text)' : undefined}
          />
        </div>

        <BandSplit data={data} onOpenStudent={onOpenStudent} />

        <div
          className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-4 py-3"
          style={{
            borderTop: '1.5px solid var(--border-strong)',
            background: 'var(--brand-lime-soft)',
          }}
        >
          <span
            className="text-xs font-bold uppercase tracking-[0.08em]"
            style={{ color: 'var(--brand-ink)' }}
          >
            Marks waiting for this class
          </span>
          <span
            data-numeric=""
            className="text-xl font-extrabold leading-none"
            style={{ color: 'var(--brand-ink)' }}
          >
            {footer.marksLost}
          </span>
          <span className="text-xs" style={{ color: 'var(--ink-2)' }}>
            {footer.marksLostNote}
          </span>
        </div>

        <BandLegend actions={BAND_ACTION_FULL} order={TEACHER_BIN_ORDER} />

        {/* The two things the five band lines above do not say: what is true of
          every band, and what order to do them in. The order differs by who is
          reading — that is the point of printing both. */}
        <div
          className="flex flex-col gap-1.5 px-4 py-3 text-fs-11"
          style={{ borderTop: '1px solid var(--border-subtle)', background: 'var(--paper-sunk)' }}
        >
          <p style={{ color: 'var(--ink-2)' }}>{STANDING_NOTE}</p>
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            <WorkOrder label="You work" order={WORK_ORDER.teacher} />
            <WorkOrder label="They work" order={WORK_ORDER.student} note={WORK_ORDER.studentNote} />
          </div>
        </div>
      </SheetFrame>

      {/* ── Graphical analysis ── */}
      <SheetFrame id="cwy-class-action-plan-chart">
        <div className="px-4 py-3" style={{ borderBottom: '1.5px solid var(--border-strong)' }}>
          <h3 className="text-sm font-bold" style={{ color: 'var(--text-strong)' }}>
            Graphical analysis
          </h3>
          <p className="mt-0.5 text-xs" style={{ color: 'var(--ink-3)' }}>
            How the class split across the five bands, topic by topic.
          </p>
        </div>
        <div className="px-4 py-4">
          <TopicBandChart topics={topics} />
        </div>
      </SheetFrame>

      <HelplineStack
        paperId={paperId}
        classroomId={classroomId}
        panels={panels}
        onClose={closeHelpline}
        onCloseAll={() => setPanels([])}
      />
    </>
  );
}
